import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
  useRef,
} from 'react'
import { jwtDecode } from 'jwt-decode'
import {
  useAuthUserinfoRetrieve,
  useAuthValidateTokenCreate,
} from '../api/generated/endpoints/auth/auth'
import {
  clearAccessToken,
  goToLogin,
  logoutOnServer,
  refreshAccessToken,
  setOnUnauthorized,
} from '../api/axios-instance'
import { useQueryClient } from '@tanstack/react-query'

interface UserData {
  id: string
  name: string
  email: string
  avatar?: string
  phone?: string
  address?: string
  scope: string
  role: string
  client?: string
}

interface AuthState {
  isLoading: boolean
  error: string | null
}

interface LoginCredentials {
  email: string
  password: string
}

interface AuthContextType {
  isAuthenticated: boolean
  user: UserData | null
  login: (credentials: LoginCredentials) => Promise<boolean>
  logout: (redirectUrl?: string) => void
  hasScope: (requiredScopes: string | string[]) => boolean
  isAdmin: boolean
  isClient: boolean
  isClientUser: boolean
  isClientManager: boolean
  isLoading: boolean
  error: string | null
  refreshToken: () => Promise<boolean>
}

interface AuthProviderProps {
  children: ReactNode
}

interface JWTPayload {
  scope: string
  role: string
  client?: string
  exp: number
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const TOKEN_REFRESH_MARGIN = 2 * 60 * 1000 // Refresh 2 minutes before expiry
const TOKEN_KEY = 'token'

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const queryClient = useQueryClient()
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)
  const [userData, setUserData] = useState<UserData | null>(null)
  const [authState, setAuthState] = useState<AuthState>({
    isLoading: true,
    error: null,
  })
  const [token, setToken] = useState<string | null>(null)
  const [shouldValidate, setShouldValidate] = useState<boolean>(false)
  const [shouldFetchUser, setShouldFetchUser] = useState<boolean>(false)
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isRefreshingRef = useRef<boolean>(false)

  const { mutate: validateToken } = useAuthValidateTokenCreate()
  const {
    data: userInfoData,
    isError: isUserInfoError,
    refetch: refetchUserInfo,
  } = useAuthUserinfoRetrieve({
    query: {
      enabled: shouldFetchUser,
    },
  })

  // Clear refresh timeout on unmount
  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current)
      }
    }
  }, [])

  // Schedule token refresh based on expiration
  const scheduleTokenRefresh = useCallback((accessToken: string) => {
    try {
      const decoded = jwtDecode<JWTPayload>(accessToken)
      const expiresAt = decoded.exp * 1000 // Convert to milliseconds
      const now = Date.now()
      const timeUntilRefresh = expiresAt - now - TOKEN_REFRESH_MARGIN

      // Clear existing timeout
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current)
      }

      // Only schedule if token isn't already expired
      if (timeUntilRefresh > 0) {
        refreshTimeoutRef.current = setTimeout(() => {
          performTokenRefresh()
        }, timeUntilRefresh)
      } else {
        // Token is expired or about to expire, refresh immediately
        performTokenRefresh()
      }
    } catch (error) {
      console.error('Failed to schedule token refresh:', error)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Perform token refresh
  const performTokenRefresh = useCallback(async (): Promise<boolean> => {
    if (isRefreshingRef.current) {
      return false
    }

    isRefreshingRef.current = true
    const hadAccess = Boolean(localStorage.getItem(TOKEN_KEY))
    const newAccessToken = await refreshAccessToken()
    isRefreshingRef.current = false

    if (newAccessToken) {
      setToken(newAccessToken)
      setIsAuthenticated(true)
      setShouldFetchUser(true)
      scheduleTokenRefresh(newAccessToken)
      return true
    }

    clearAccessToken()
    if (hadAccess) {
      handleLogout()
    } else {
      setIsAuthenticated(false)
      setUserData(null)
      setToken(null)
      setAuthState({ isLoading: false, error: null })
    }
    return false
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Initialize token from localStorage
  useEffect(() => {
    localStorage.removeItem('refresh_token')
    const storedToken = localStorage.getItem(TOKEN_KEY)

    if (storedToken) {
      setToken(storedToken)
      setShouldValidate(true)
      scheduleTokenRefresh(storedToken)
    } else {
      performTokenRefresh().then((success) => {
        if (!success) {
          setAuthState({ isLoading: false, error: null })
        }
      })
    }
  }, [scheduleTokenRefresh, performTokenRefresh])

  // Handle token validation on mount or token change
  useEffect(() => {
    if (shouldValidate && token) {
      validateToken(
        { data: { token } },
        {
          onSuccess: () => {
            setIsAuthenticated(true)
            localStorage.setItem(TOKEN_KEY, token)
            setShouldFetchUser(true)
            setShouldValidate(false)
          },
          onError: () => {
            performTokenRefresh().then((success) => {
              if (!success) {
                setAuthState({
                  isLoading: false,
                  error: 'Token validation failed.',
                })
              }
            })
            setShouldValidate(false)
          },
        },
      )
    }
  }, [shouldValidate, token, validateToken, performTokenRefresh])

  // Handle user info data when it's fetched
  useEffect(() => {
    if (!shouldFetchUser || !userInfoData?.data) {
      return
    }

    const profile = userInfoData.data
    setUserData({
      id: profile.id?.toString() ?? '',
      name: profile.name ?? '',
      email: profile.email ?? '',
      avatar: profile.avatar ?? undefined,
      phone: profile.phone ?? undefined,
      address: profile.address ?? undefined,
      scope: profile.role ?? '',
      role: profile.client_role ?? '',
    })
    import('../lib/telemetry')
      .then((m) =>
        m.setTelemetryUser({
          id: profile.id?.toString() ?? '',
          email: profile.email ?? '',
        }),
      )
      .catch(() => {})
    setAuthState({ isLoading: false, error: null })
  }, [shouldFetchUser, userInfoData])

  // Fail closed if the live profile cannot be loaded
  useEffect(() => {
    if (shouldFetchUser && isUserInfoError) {
      handleLogout()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetchUser, isUserInfoError])

  const apiRequest = async (
    url: string,
    options: RequestInit = {},
  ): Promise<any> => {
    try {
      const response = await fetch(url, options)

      if (!response.ok) {
        throw await response.json()
      }

      return response.json()
    } catch (error) {
      throw error
    }
  }

  const login = async (credentials: LoginCredentials): Promise<boolean> => {
    setAuthState({ isLoading: true, error: null })
    try {
      const response = await apiRequest(
        `${process.env.REACT_APP_API_URL}/api/auth/token/`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(credentials),
        },
      )

      const { access } = response

      if (!access) {
        throw new Error('Invalid token response')
      }

      localStorage.setItem(TOKEN_KEY, access)
      localStorage.removeItem('refresh_token')

      setToken(access)
      setShouldFetchUser(true)
      scheduleTokenRefresh(access)

      const profileResult = await refetchUserInfo()
      const profile = profileResult.data?.data
      if (profileResult.error || !profile) {
        clearAccessToken()
        setIsAuthenticated(false)
        setToken(null)
        setShouldFetchUser(false)
        setAuthState({
          isLoading: false,
          error: 'Failed to load profile.',
        })
        return false
      }

      setUserData({
        id: profile.id?.toString() ?? '',
        name: profile.name ?? '',
        email: profile.email ?? '',
        avatar: profile.avatar ?? undefined,
        phone: profile.phone ?? undefined,
        address: profile.address ?? undefined,
        scope: profile.role ?? '',
        role: profile.client_role ?? '',
      })
      setIsAuthenticated(true)
      setAuthState({ isLoading: false, error: null })

      return true
    } catch (error: any) {
      const errorMessage =
        error?.password?.[0] ||
        error?.username?.[0] ||
        error?.email?.[0] ||
        error?.detail ||
        error?.message ||
        'Invalid credentials'

      setAuthState({ isLoading: false, error: errorMessage })

      return false
    }
  }

  const handleLogout = useCallback((redirectUrl: string = '/login'): void => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current)
    }

    import('../lib/telemetry')
      .then((m) => m.setTelemetryUser(null))
      .catch(() => {})

    void logoutOnServer()
    clearAccessToken()
    queryClient.removeQueries()
    setIsAuthenticated(false)
    setUserData(null)
    setToken(null)
    setShouldFetchUser(false)
    setShouldValidate(false)
    setAuthState({ isLoading: false, error: null })
    isRefreshingRef.current = false
    goToLogin(redirectUrl || '/login')
  }, [queryClient])

  useEffect(() => {
    setOnUnauthorized(() => handleLogout())
    return () => setOnUnauthorized(null)
  }, [handleLogout])

  const hasScope = (requiredScopes: string | string[]): boolean => {
    if (!userData?.scope) return false
    const scopes = Array.isArray(requiredScopes)
      ? requiredScopes
      : [requiredScopes]

    return scopes.some((scope) => userData.scope === scope)
  }

  const isAdmin = userData?.scope
    ? ['owner', 'admin'].includes(userData.scope)
    : false
  const isClient = userData?.scope ? userData.scope === 'client' : false
  const isClientUser =
    userData?.scope && userData?.role
      ? userData.scope === 'client' && userData.role === 'user'
      : false
  const isClientManager =
    userData?.scope && userData?.role
      ? userData.scope === 'client' && userData.role === 'manager'
      : false

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user: userData,
        login,
        logout: handleLogout,
        hasScope,
        isAdmin,
        isClient,
        isClientUser,
        isClientManager,
        isLoading: authState.isLoading,
        error: authState.error,
        refreshToken: performTokenRefresh,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  return context
}
